/**
 * Local IndexedDB-backed cache of profile keys we have been granted.
 *
 * Each record holds the 32-byte ProfileKey raw bytes and a recordedAt
 * timestamp; we do not keep any history of old keys, so once a peer
 * rotates and the new key is recorded, their previously cached blobs
 * become unreadable (they would have to redistribute the old blob under
 * the new key, which is what ProfileKeyRotation produces). This is the
 * desired property — it prevents replay of a stale profile blob by a
 * hostile cache.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ProfileKeyStore.swift`.
 * iOS uses file-per-owner under .completeFileProtection; web uses
 * IndexedDB (the SubtleCrypto + non-extractable CryptoKey analogue).
 *
 * Design note: the store is intentionally injectable (dbName param) so
 * self-tests can use isolated namespaces without polluting production state.
 */

import { ProfileKey, ProfileKeyError } from "./ProfileKey";
import {
  ProfileKeyDistributionPayload,
  DISTRIBUTION_SCHEMA_VERSION,
} from "./ProfileKeyDistribution";

// ---------------------------------------------------------------------------
// Record type (what we persist)
// ---------------------------------------------------------------------------

export interface ProfileKeyRecord {
  ownerUserId: string;
  /** Base64-encoded raw 32-byte profile key. */
  profileKeyB64: string;
  recordedAt: number; // Unix seconds
}

// ---------------------------------------------------------------------------
// IndexedDB constants
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether:profile-key-store:v1";
const STORE_NAME = "profile-keys";
const INDEX_OWNER = "ownerUserId";

// ---------------------------------------------------------------------------
// ProfileKeyStore
// ---------------------------------------------------------------------------

export class ProfileKeyStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  // -------------------------------------------------------------------------
  // Private: open DB
  // -------------------------------------------------------------------------

  private openDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, 1);
      req.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: "ownerUserId",
          });
          store.createIndex(INDEX_OWNER, "ownerUserId", { unique: true });
        }
      };
      req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
      req.onerror = () => reject(req.error);
    });
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // Recording
  // -------------------------------------------------------------------------

  /**
   * Stores or replaces the profile key for an owner.
   * Returns true if the write changed state (new key or different bytes).
   * Returns false if the existing record already matched (no churn).
   */
  async record(
    ownerUserId: string,
    key: ProfileKey,
    recordedAt?: number,
  ): Promise<boolean> {
    const db = await this.openDB();
    const newB64 = key.toBase64();
    const ts = recordedAt ?? Math.floor(Date.now() / 1000);

    return new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);

      const getReq = store.get(ownerUserId);
      getReq.onsuccess = () => {
        const existing = getReq.result as ProfileKeyRecord | undefined;
        if (existing && existing.profileKeyB64 === newB64) {
          resolve(false);
          return;
        }
        const record: ProfileKeyRecord = {
          ownerUserId,
          profileKeyB64: newB64,
          recordedAt: ts,
        };
        const putReq = store.put(record);
        putReq.onsuccess = () => resolve(true);
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Convenience: builds a ProfileKey from a distribution payload and
   * forwards to the primary record(...). Validates the 32-byte length
   * (codec already does, but defence in depth).
   */
  async recordDistribution(
    distribution: ProfileKeyDistributionPayload,
    recordedAt?: number,
  ): Promise<boolean> {
    if (distribution.schema !== DISTRIBUTION_SCHEMA_VERSION) {
      throw new ProfileKeyError(
        "unsupportedSchema",
        `unsupported schema: ${distribution.schema}`,
      );
    }
    const key = new ProfileKey(distribution.profileKey);
    return this.record(distribution.ownerUserId, key, recordedAt);
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  /** Returns the profile key for ownerUserId, or null if not cached. */
  async key(ownerUserId: string): Promise<ProfileKey | null> {
    const db = await this.openDB();
    return new Promise<ProfileKey | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(ownerUserId);
      req.onsuccess = () => {
        const rec = req.result as ProfileKeyRecord | undefined;
        if (!rec) {
          resolve(null);
          return;
        }
        try {
          resolve(ProfileKey.fromBase64(rec.profileKeyB64));
        } catch {
          resolve(null);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Sorted lexicographic list of every userId for which we hold a
   * profile key. Used by rotation to fan out redistribution.
   */
  async allKnown(): Promise<string[]> {
    const db = await this.openDB();
    return new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => {
        const records = req.result as ProfileKeyRecord[];
        resolve(records.map((r) => r.ownerUserId).sort());
      };
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // Mutation
  // -------------------------------------------------------------------------

  /**
   * Drops the cached key for a single owner. Subsequent profile blobs
   * from that owner will fail to decrypt (which is the point).
   */
  async forget(ownerUserId: string): Promise<void> {
    const db = await this.openDB();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(ownerUserId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  /** Drops all cached keys. */
  async resetAll(): Promise<void> {
    const db = await this.openDB();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Delete the underlying IndexedDB database entirely.
   * Used by tests to clean up after isolated DB namespaces.
   */
  async deleteDB(): Promise<void> {
    // Close any open connection first.
    if (this.dbPromise) {
      try {
        const db = await this.dbPromise;
        db.close();
      } catch {
        // ignore
      }
      this.dbPromise = null;
    }
    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(this.dbName);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      req.onblocked = () => resolve(); // Safari may fire this; resolve anyway
    });
  }
}
